/**
 * Rota ağacı — KOD TABANLI, dosya tabanlı değil.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * NEDEN KOD TABANLI
 *
 * TanStack Router'ın dosya tabanlı kipi bir üreteç çalıştırıp `routeTree.gen.ts`
 * yazar. Bu dosya lint, format ve typecheck zincirimize giren ama ELLE
 * YAZILMAMIŞ bir dosya olurdu — ve üretecin çıktısıyla kaynak arasında
 * senkronizasyon sorunu doğar. Kod tabanlı tanım aynı tip güvenliğini
 * üreteç olmadan verir.
 *
 * Tip güvenliğinin karşılığı somut: `<Link to="/sahalarr">` DERLENMEZ,
 * ve parametre adı yanlış yazılırsa derleyici yakalar [K-020].
 * ═══════════════════════════════════════════════════════════════════════════
 */
import {
  Outlet,
  createMemoryHistory,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
} from '@tanstack/react-router';

import { AppShell } from './shell/AppShell.tsx';
import { LoginPage } from './auth/LoginPage.tsx';
import { PanoListesi } from './field/PanoListesi.tsx';
import { ProfilePage } from './auth/ProfilePage.tsx';
import { RequireSession } from './auth/RequireSession.tsx';
import { Arama } from './field/Arama.tsx';
import { PaylasimLinkleri } from './share/PaylasimLinkleri.tsx';
import { PublicIzleme } from './share/PublicIzleme.tsx';
import { Denetim } from './field/Denetim.tsx';
import { OkumaListesi } from './field/OkumaListesi.tsx';
import { PanoDuzenle, PanoYeni } from './field/PanoFormu.tsx';
import { SahaDetay } from './field/SahaDetay.tsx';
import { SahaDuzenle, SahaYeni } from './field/SahaFormu.tsx';
import { SahaListesi } from './field/SahaListesi.tsx';
import { TankDuzenle, TankYeni } from './field/TankFormu.tsx';
import { TankListesi } from './field/TankListesi.tsx';
import { Gallery } from './design/Gallery.tsx';

/*
 * ─── İKİ KATMANLI KÖK ────────────────────────────────────────────────────────
 * Giriş ekranının kabuğu (kenar çubuğu / alt sekme) OLMAMALI: oturumu
 * olmayan kullanıcıya gidemeyeceği bağlantıları göstermek anlamsız.
 *
 * Bu yüzden kök yalnız `<Outlet/>`; kabuk KORUMALI dalda yaşıyor.
 */
const rootRoute = createRootRoute({ component: () => <Outlet /> });

/** Oturum gerektiren her şeyin ebeveyni — kabuk burada. */
const korumaliRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'korumali',
  component: () => (
    <RequireSession>
      <AppShell />
    </RequireSession>
  ),
});

/** Giriş ekranının arama parametreleri — ikisi de İSTEĞE BAĞLI. */
export interface GirisArama {
  /** Giriş sonrası dönülecek yol. */
  redirect?: string;
  /** Oturum kullanılırken düştü (S37) — yalnız bilgi mesajı içindir. */
  sona_erdi?: boolean;
}

/**
 * Giriş rotasının arama doğrulayıcısı — DIŞA AÇIK, çünkü testler kendi
 * yönlendiricilerini kurarken bunun KOPYASINI yazarsa açık yönlendirme
 * koruması test edilen kodun dışında kalır ve sessizce ayrışabilir.
 */
export function girisAramaDogrula(search: Record<string, unknown>): GirisArama {
  const r = search['redirect'];
  /*
   * `sona_erdi` yalnız MESAJ gösterir; hiçbir yetki taşımaz, bu yüzden
   * adresten gelmesi zararsız. Gerçek oturum durumu her hâlükârda sunucudan
   * okunur [K-010].
   *
   * ÖLÇÜLDÜ: `?sona_erdi=true` yönlendiricinin çözücüsünden `boolean` olarak
   * geçiyor, metin olarak değil. İlk yazımda `=== 'true'` karşılaştırması da
   * vardı; kaldırıldığında HİÇBİR test kırmızıya dönmedi — yani ölü koddu.
   */
  const sonaErdi = search['sona_erdi'] === true;
  // AÇIK YÖNLENDİRME KORUMASI: yalnız kendi sitemizdeki YOLLAR kabul edilir.
  // `//evil.com` veya `https://evil.com` gibi değerler reddedilir — aksi
  // hâlde giriş bağlantısı kimlik avı için kullanılabilirdi.
  const yol = typeof r === 'string' && r.startsWith('/') && !r.startsWith('//') ? r : undefined;
  return {
    ...(yol === undefined ? {} : { redirect: yol }),
    ...(sonaErdi ? { sona_erdi: true } : {}),
  };
}

const girisRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/giris',
  /*
   * Arama parametresi TİPLİ doğrulanır — TanStack Router'ın asıl kazancı bu
   * [K-020]. `redirect` kullanıcıdan gelen bir değerdir; olduğu gibi
   * güvenilmez.
   */
  validateSearch: girisAramaDogrula,
  component: LoginPage,
});

/**
 * Kök — UYGULAMANIN ANA EKRANI: OKUMALAR.
 *
 * Görev 3'e kadar burası yapılandırma listesine yönlendiriyordu; o zaman
 * gösterilecek okuma yoktu. Artık ana ekran "sahalarımdan veri geliyor mu"
 * sorusunu cevaplıyor — kullanıcının günde onlarca kez sorduğu soru bu,
 * yapılandırma ise ayda birkaç kez dokunulan bir bölüm.
 */
const indexRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>): { q?: string; sessiz?: true } => ({
    ...aramaParametresi(search),
    ...(search['sessiz'] === true || search['sessiz'] === 1 ? { sessiz: true as const } : {}),
  }),
  component: OkumaListesi,
});

/**
 * Liste aramaları ADRESTE taşınır — paylaşılabilir ve geri tuşuyla gezilebilir.
 *
 * Doğrulayıcı boş dizeyi DÜŞÜRÜR: `?q=` ile `?` aynı listeyi gösterir, iki
 * ayrı önbellek girdisi oluşmasın.
 *
 * ─── SAYISAL TERİM TUZAĞI (testte yakalandı) ────────────────────────────────
 * Yönlendiricinin çözücüsü `?q=2006` ve `?q=188.38` gibi değerleri NUMBER
 * olarak çözüyor (aynı davranış `sona_erdi=true`de boolean üretiyordu). İlk
 * yazımda yalnız `typeof === 'string'` kabul ediliyordu; sonuç: IP adresi ya
 * da seri numarası araması paylaşılan bir bağlantıdan açıldığında sorgu
 * SESSİZCE düşüyor ve kullanıcı boş arama ekranı görüyordu — üstelik IP
 * araması bu görevin başlıca gerekçelerinden biri.
 */
export function aramaParametresi(search: Record<string, unknown>): { q?: string } {
  const ham = search['q'];
  const q = typeof ham === 'string' ? ham : typeof ham === 'number' ? String(ham) : undefined;
  return q !== undefined && q.trim() !== '' ? { q: q.trim() } : {};
}

const sahalarRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/sahalar',
  validateSearch: (search: Record<string, unknown>): { q?: string; musteri?: number } => {
    // `?musteri=` küresel aramadaki müşteri sonucunun hedefi — müşterinin
    // kendi ekranı yok, sahaları filtreli listeye bağlanıyor.
    const m = Number(search['musteri']);
    return {
      ...aramaParametresi(search),
      ...(Number.isInteger(m) && m > 0 ? { musteri: m } : {}),
    };
  },
  component: SahaListesi,
});

const sahaDetayRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/sahalar/$sahaId',
  component: SahaDetay,
});

/*
 * ─── FORM ROTALARI ───────────────────────────────────────────────────────────
 * `/yeni` statik, `/$sahaId` dinamik: TanStack Router statiği önce eşler, yani
 * `/sahalar/yeni` asla "id'si `yeni` olan saha" diye yorumlanmaz.
 *
 * `?siteId=` yalnız formu ÖN DOLDURUR (saha detayından "pano ekle" ile
 * gelindiğinde). Yetki taşımaz; kaydın firması her hâlükârda oturumdan
 * belirlenir [K-010].
 */
function sahaParametresi(search: Record<string, unknown>): { siteId?: number } {
  const ham = search['siteId'];
  const id = typeof ham === 'number' ? ham : Number(ham);
  return Number.isInteger(id) && id > 0 ? { siteId: id } : {};
}

const sahaYeniRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/sahalar/yeni',
  component: SahaYeni,
});

const sahaDuzenleRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/sahalar/$sahaId/duzenle',
  component: SahaDuzenle,
});

const panoYeniRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/panolar/yeni',
  validateSearch: sahaParametresi,
  component: PanoYeni,
});

const panoDuzenleRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/panolar/$panoId/duzenle',
  component: PanoDuzenle,
});

const tankYeniRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/tanklar/yeni',
  validateSearch: sahaParametresi,
  component: TankYeni,
});

const tankDuzenleRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/tanklar/$tankId/duzenle',
  component: TankDuzenle,
});

/*
 * ─── PUBLIC İZLEME — KORUMALI AĞACIN DIŞINDA ─────────────────────────────────
 * Kök rotanın DOĞRUDAN çocuğu: `korumaliRoute` altında olsaydı `RequireSession`
 * çalışır ve oturumsuz ziyaretçi girişe atılırdı. Bu, sistemin tek kimliksiz
 * kapısı; yeri de o yüzden ayrı.
 */
const izlemeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/izleme/$token',
  component: PublicIzleme,
});

const paylasimRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/paylasim',
  component: PaylasimLinkleri,
});

const denetimRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/denetim',
  component: Denetim,
});

const panolarRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/panolar',
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; mod?: 'public' | 'vpn'; yoklama?: 'acik' | 'kapali' } => {
    const mod = search['mod'];
    const yoklama = search['yoklama'];
    return {
      ...aramaParametresi(search),
      ...(mod === 'public' || mod === 'vpn' ? { mod } : {}),
      ...(yoklama === 'acik' || yoklama === 'kapali' ? { yoklama } : {}),
    };
  },
  component: PanoListesi,
});

const tanklarRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/tanklar',
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; depoda?: true; kapasite?: number } => {
    const k = Number(search['kapasite']);
    return {
      ...aramaParametresi(search),
      // Yalnız `true` anlamlı: `?depoda=0` filtre YOK demektir, filtre değil.
      ...(search['depoda'] === true || search['depoda'] === 1 ? { depoda: true as const } : {}),
      ...(Number.isInteger(k) && k > 0 ? { kapasite: k } : {}),
    };
  },
  component: TankListesi,
});

/*
 * ─── HARİTA TEMBEL YÜKLENİYOR ────────────────────────────────────────────────
 * Leaflet + CSS'i ana pakete koymak, haritaya hiç girmeyen kullanıcıya da o
 * yükü ödetirdi. Mobil öncelikli bir üründe [K-020] ana ekranın ağırlığı
 * doğrudan açılış süresidir; ölçüm Görev 6'da yapıldı ve rakamlar STATUS'ta.
 *
 * `lazyRouteComponent` yönlendiricinin kendi bölme mekanizması: rota
 * eşleştiğinde chunk indiriliyor.
 */
const haritaRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/harita',
  component: lazyRouteComponent(() => import('./field/Harita.tsx'), 'Harita'),
});

const araRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/ara',
  validateSearch: aramaParametresi,
  component: Arama,
});

const profilRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/profil',
  component: ProfilePage,
});

/** Tasarım sistemi galerisi — geliştirme aracı, üretimde de zararsız. */
const tasarimRoute = createRoute({
  getParentRoute: () => korumaliRoute,
  path: '/tasarim',
  component: Gallery,
});

const routeTree = rootRoute.addChildren([
  girisRoute,
  izlemeRoute,
  korumaliRoute.addChildren([
    indexRoute,
    sahalarRoute,
    sahaYeniRoute,
    sahaDetayRoute,
    sahaDuzenleRoute,
    panolarRoute,
    panoYeniRoute,
    panoDuzenleRoute,
    tanklarRoute,
    tankYeniRoute,
    tankDuzenleRoute,
    denetimRoute,
    paylasimRoute,
    haritaRoute,
    araRoute,
    profilRoute,
    tasarimRoute,
  ]),
]);

/*
 * ─── ÖNİZLEME KİPİ ───────────────────────────────────────────────────────────
 * `VITE_PREVIEW` ile derlendiğinde yönlendirici BELLEK geçmişi kullanır.
 * Sebep: uygulama kök dizinde değil, gömülü bir sayfada çalıştığında tarayıcı
 * yolu hiçbir rotayla eşleşmez ve açılışta 404 görünür. Bellek geçmişi bunu
 * ortadan kaldırır — gezinme çalışır, tarayıcı adresi değişmez.
 *
 * Üretim derlemesinde bu dal HİÇ ÇALIŞMAZ (ölü kod olarak elenir).
 */
const preview = import.meta.env.VITE_PREVIEW === '1';

export const router = createRouter({
  routeTree,
  ...(preview ? { history: createMemoryHistory({ initialEntries: ['/sahalar'] }) } : {}),
});

/** Rota tipleri global olarak bildirilir — `<Link to>` böylece denetlenir. */
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
